<template>
    <v-form ref="form" @submit.prevent="save">
        <v-card>
            <card-header :title="cardTitle" />
            <v-card-text>
                <v-row>
                    <v-col cols="12" xs="12" sm="12" md="6">
                        <v-text-field
                            v-model="model.name"
                            :label="$t('resources.category.attributes.name')"
                            :hint="$t('resources.category.hints.name')"
                            :counter="18"
                            :error-messages="nameErrors"
                            @input="$v.model.name.$touch()"
                            @blur="$v.model.name.$touch()"
                        />
                    </v-col>

                    <v-col cols="12" xs="12" sm="6" md="3">
                        <v-text-field
                            v-model="model.percent"
                            type="number"
                            v-bind="$t('percent-config')"
                            :label="$t('resources.category.attributes.percent')"
                            :hint="$t('resources.category.hints.percent')"
                            :error-messages="percentErrors"
                            @input="$v.model.percent.$touch()"
                            @blur="$v.model.percent.$touch()"
                        />
                    </v-col>

                    <v-col cols="12" xs="12" sm="6" md="3">
                        <v-text-field
                            v-model="model.discount_percentage"
                            type="number"
                            v-bind="$t('percent-config')"
                            :label="
                                $t(
                                    'resources.category.attributes.discount_percentage',
                                )
                            "
                            :hint="
                                $t(
                                    'resources.category.hints.discount_percentage',
                                )
                            "
                            :error-messages="discountPercentageErrors"
                            @input="$v.model.discount_percentage.$touch()"
                            @blur="$v.model.discount_percentage.$touch()"
                        />
                    </v-col>

                    <v-col cols="12" xs="12" sm="12" md="12">
                        <v-select
                            v-model="model.reseller_type_ids"
                            :items="resellerTypes"
                            :label="
                                $t(
                                    'resources.category.attributes.reseller_type_ids',
                                )
                            "
                            multiple
                            chips
                            deletable-chips
                            clearable
                            item-text="name"
                            item-value="id"
                        />
                    </v-col>

                    <!-- ICON COL -->
                    <v-col cols="12" xs="12" sm="12" md="4" lg="4" xl="4">
                        <div class="grey lighten-4">
                            <v-col cols="12" xs="12" sm="12" md="12">
                                <color-input
                                    :label="
                                        $t(
                                            'resources.category.attributes.badge_color',
                                        )
                                    "
                                    v-model="model.badge_color"
                                    :errorMessages="badgeColorErrors"
                                    @input="$v.model.badge_color.$touch()"
                                    @blur="$v.model.badge_color.$touch()"
                                />
                            </v-col>

                            <v-col cols="12" xs="12" sm="12" md="12">
                                <v-label>
                                    {{
                                        $t(
                                            'resources.category.attributes.badge_icon',
                                        )
                                    }}
                                </v-label>
                                <v-radio-group
                                    v-model="model.badge_icon"
                                    row
                                    class="justify-center"
                                    :errorMessages="badgeIconErrors"
                                    @input="$v.model.badge_icon.$touch()"
                                    @blur="$v.model.badge_icon.$touch()"
                                >
                                    <v-radio
                                        v-for="icon in model.availableIcons"
                                        :key="icon"
                                        :value="icon"
                                    >
                                        <template #label>
                                            <v-icon
                                                :color="category.badge_color"
                                                large
                                            >
                                                {{ icon }}
                                            </v-icon>
                                        </template>
                                    </v-radio>
                                </v-radio-group>
                            </v-col>
                        </div>
                    </v-col>
                    <!-- /ICON COL -->

                    <v-col cols="12" xs="12" sm="12" md="8" lg="8" xl="8">
                        <v-label>{{
                            $t('resources.category.attributes.description')
                        }}</v-label>

                        <tiptap-vuetify
                            v-model="model.description"
                            :toolbar-attributes="{ color: 'grey lighten-3' }"
                            :extensions="tiptap.extensions"
                        ></tiptap-vuetify>
                        <div class="v-text-field__details">
                            <div class="v-messages theme--light">
                                <div class="v-messages__wrapper">
                                    {{
                                        $t(
                                            'resources.category.hints.description',
                                        )
                                    }}
                                </div>
                            </div>
                        </div>
                    </v-col>
                </v-row>
            </v-card-text>
            <form-actions :loadingSave="loading" />
        </v-card>
    </v-form>
</template>

<script>
import FormActions from '@/shared/components/FormActions'
import ColorInput from '@/shared/inputs/ColorInput'
import Category from '@/models/Category'
import { validationMixin } from 'vuelidate'
import {
    required,
    requiredIf,
    maxLength,
    between,
} from 'vuelidate/lib/validators'
import {
    TiptapVuetify,
    Heading,
    Bold,
    Italic,
    Strike,
    Underline,
    Code,
    Paragraph,
    BulletList,
    OrderedList,
    ListItem,
    Link,
    Blockquote,
    HardBreak,
    HorizontalRule,
    History,
} from 'tiptap-vuetify'

export default {
    props: {
        category: {
            type: Object,
            required: true,
            validator: category => {
                return category instanceof Category
            },
        },

        resellerTypes: {
            type: Array,
            required: true,
        },

        titlePrefix: {
            type: String,
            default: '',
        },
    },

    components: {
        ColorInput,
        TiptapVuetify,
        FormActions,
    },

    data() {
        return {
            loading: false,
            tiptap: {
                extensions: [
                    History,
                    Blockquote,
                    Link,
                    Underline,
                    Strike,
                    Italic,
                    ListItem,
                    BulletList,
                    OrderedList,
                    [
                        Heading,
                        {
                            options: {
                                levels: [1, 2, 3],
                            },
                        },
                    ],
                    Bold,
                    Code,
                    HorizontalRule,
                    Paragraph,
                    HardBreak,
                ],
                editor: null,
            },
        }
    },

    mixins: [validationMixin],

    validations() {
        return {
            model: {
                name: { required, maxLength: maxLength(18) },
                percent: {
                    requiredIf: requiredIf(() => {
                        if (!this.model.reseller_type_ids) return false
                        let required = false
                        this.model.reseller_type_ids.forEach(row => {
                            let type = this.resellerTypes.find(
                                type => type.id == row,
                            )

                            if (!type.billable) {
                                required = true
                            }
                        })

                        return required
                    }),
                    between: between(1, 50),
                },
                discount_percentage: {
                    requiredIf: requiredIf(() => {
                        if (!this.model.reseller_type_ids) return false
                        let required = false
                        this.model.reseller_type_ids.forEach(row => {
                            let type = this.resellerTypes.find(
                                type => type.id == row,
                            )

                            if (type.billable) {
                                required = true
                            }
                        })

                        return required
                    }),
                    between: between(0, 50),
                },
                badge_color: { required },
                badge_icon: { required },
            },
        }
    },

    computed: {
        cardTitle() {
            return this.$t('forms.title', {
                prefix: this.titlePrefix,
                resource: this.$tc('resources.category.name', 1).toLowerCase(),
            })
        },

        model() {
            return this.category
        },

        nameErrors() {
            const errors = []
            if (!this.$v.model.name.$dirty) return errors

            !this.$v.model.name.maxLength &&
                errors.push(
                    this.$t('forms.errors.maxLength', {
                        label: this.$t(
                            'resources.category.attributes.name',
                        ).toLowerCase(),
                        length: 18,
                    }),
                )
            !this.$v.model.name.required &&
                errors.push(
                    this.$t('forms.errors.required', {
                        label: this.$t(
                            'resources.category.attributes.name',
                        ).toLowerCase(),
                    }),
                )

            return errors
        },

        percentErrors() {
            const errors = []
            if (!this.$v.model.percent.$dirty) return errors

            !this.$v.model.percent.between &&
                errors.push(
                    this.$t('forms.errors.between', {
                        label: this.$t(
                            'resources.category.attributes.percent',
                        ).toLowerCase(),
                        min: 1,
                        max: 50,
                    }),
                )
            !this.$v.model.percent.requiredIf &&
                errors.push(
                    this.$t('forms.errors.required', {
                        label: this.$t(
                            'resources.category.attributes.percent',
                        ).toLowerCase(),
                    }),
                )

            return errors
        },

        discountPercentageErrors() {
            const errors = []
            if (!this.$v.model.discount_percentage.$dirty) return errors

            !this.$v.model.discount_percentage.between &&
                errors.push(
                    this.$t('forms.errors.between', {
                        label: this.$t(
                            'resources.category.attributes.discount_percentage',
                        ).toLowerCase(),
                        min: 1,
                        max: 50,
                    }),
                )
            !this.$v.model.discount_percentage.requiredIf &&
                errors.push(
                    this.$t('forms.errors.required', {
                        label: this.$t(
                            'resources.category.attributes.discount_percentage',
                        ).toLowerCase(),
                    }),
                )

            return errors
        },

        badgeColorErrors() {
            const errors = []
            if (!this.$v.model.badge_color.$dirty) return errors

            !this.$v.model.badge_color.required &&
                errors.push(
                    this.$t('forms.errors.required', {
                        label: this.$t(
                            'resources.category.attributes.badge_color',
                        ).toLowerCase(),
                    }),
                )

            return errors
        },

        badgeIconErrors() {
            const errors = []
            if (!this.$v.model.badge_icon.$dirty) return errors

            !this.$v.model.badge_icon.required &&
                errors.push(
                    this.$t('forms.errors.required', {
                        label: this.$t(
                            'resources.category.attributes.badge_icon',
                        ).toLowerCase(),
                    }),
                )

            return errors
        },
    },

    methods: {
        async save() {
            this.loading = true

            this.$v.$touch()

            if (this.$v.$invalid) {
                this.loading = false
                return
            }

            let res = await this.model.save()
            this.loading = false
            if (!res) return

            this.$bus.$emit(
                'notify',
                'success',
                this.$t('forms.messages.saved', {
                    resource: this.$tc(
                        'resources.category.name',
                        1,
                    ).toLowerCase(),
                    name: this.model.name,
                }),
            )

            this.$emit('saved')
        },
    },
}
</script>

<style></style>
